/**
 * m3 echo Frontend - Mandantenverwaltung
 *
 * Seite zur Verwaltung von Mandanten (CRUD-Operationen).
 */

import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { tenantApi, TenantWithStats, TenantCreate, TenantUpdate } from '../../services/admin';
import TenantTable from '../../components/admin/TenantTable';
import TenantDialog from '../../components/admin/TenantDialog';
import TenantDetailDialog from '../../components/admin/TenantDetailDialog';

interface TenantManagementProps {
  embedded?: boolean;
}

export const TenantManagement: React.FC<TenantManagementProps> = ({ embedded = false }) => {
  const [tenants, setTenants] = useState<TenantWithStats[]>([]);
  const [filteredTenants, setFilteredTenants] = useState<TenantWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog-Status
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedTenant, setSelectedTenant] = useState<TenantWithStats | null>(null);

  // Löschbestätigungs-Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<TenantWithStats | null>(null);

  // Detail-Dialog (v4.1.0)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTenantForView, setSelectedTenantForView] = useState<TenantWithStats | null>(null);

  // Toast-Benachrichtigung
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    // Mandanten basierend auf Suchanfrage filtern
    if (searchQuery.trim() === '') {
      setFilteredTenants(tenants);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = tenants.filter(
        (tenant) =>
          tenant.name.toLowerCase().includes(query) ||
          tenant.display_name.toLowerCase().includes(query)
      );
      setFilteredTenants(filtered);
    }
  }, [searchQuery, tenants]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tenantApi.getAll();
      setTenants(response.data);
      setFilteredTenants(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Mandanten:', err);
      setError(err.message || 'Fehler beim Laden der Mandanten');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setDialogMode('create');
    setSelectedTenant(null);
    setDialogOpen(true);
  };

  const handleEdit = (tenant: TenantWithStats) => {
    setDialogMode('edit');
    setSelectedTenant(tenant);
    setDialogOpen(true);
  };

  const handleDeleteClick = (tenant: TenantWithStats) => {
    setTenantToDelete(tenant);
    setDeleteDialogOpen(true);
  };

  const handleViewDetails = (tenant: TenantWithStats) => {
    setSelectedTenantForView(tenant);
    setDetailDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tenantToDelete) return;

    try {
      await tenantApi.delete(tenantToDelete.id);
      showToast(`Mandant "${tenantToDelete.display_name}" erfolgreich gelöscht`);
      setDeleteDialogOpen(false);
      setTenantToDelete(null);
      loadTenants();
    } catch (err: any) {
      console.error('Fehler beim Löschen des Mandanten:', err);
      setError(err.message || 'Fehler beim Löschen des Mandanten');
      setDeleteDialogOpen(false);
    }
  };

  const handleToggleStatus = async (tenant: TenantWithStats) => {
    try {
      await tenantApi.update(tenant.id, { is_active: !tenant.is_active });
      showToast(
        tenant.is_active
          ? `Mandant "${tenant.display_name}" wurde deaktiviert`
          : `Mandant "${tenant.display_name}" wurde aktiviert`
      );
      loadTenants();
    } catch (err: any) {
      console.error('Fehler beim Ändern des Status:', err);
      setError(err.response?.data?.detail || err.message || 'Fehler beim Ändern des Status');
    }
  };

  const handleSave = async (data: TenantCreate | TenantUpdate) => {
    if (dialogMode === 'create') {
      await tenantApi.create(data as TenantCreate);
      showToast('Mandant erfolgreich erstellt');
    } else if (selectedTenant) {
      await tenantApi.update(selectedTenant.id, data as TenantUpdate);
      showToast('Mandant erfolgreich aktualisiert');
    }
    loadTenants();
  };

  const showToast = (message: string) => {
    setToastMessage(message);
    setToastOpen(true);
  };

  const Wrapper = embedded ? React.Fragment : Container;
  const wrapperProps = embedded ? {} : { maxWidth: 'lg' as const, sx: { mt: 4, mb: 4 } };

  if (loading) {
    return (
      <Wrapper {...wrapperProps}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper {...wrapperProps}>
      {/* Kopfzeile */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!embedded && (
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Mandantenverwaltung
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Alle Mandanten im System verwalten
            </Typography>
          </Box>
        )}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={embedded ? { ml: 'auto' } : {}}
        >
          Mandant Erstellen
        </Button>
      </Box>

      {/* Fehler-Hinweis */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Suchleiste */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Mandanten nach Name durchsuchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
      </Box>

      {/* Mandanten-Tabelle */}
      <TenantTable
        tenants={filteredTenants}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        onView={handleViewDetails}
        onToggleStatus={handleToggleStatus}
      />

      {/* Erstellen/Bearbeiten-Dialog */}
      <TenantDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        tenant={selectedTenant}
        mode={dialogMode}
      />

      {/* Löschbestätigungs-Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Mandant Löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie den Mandanten "{tenantToDelete?.display_name}" löschen möchten?
            Diese Aktion kann nicht rückgängig gemacht werden und betrifft alle Benutzer und Daten, die mit diesem Mandanten verbunden sind.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail-Dialog (v4.1.0) */}
      <TenantDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        tenant={selectedTenantForView}
        onQuotaChanged={loadTenants}
      />

      {/* Toast-Benachrichtigung */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Wrapper>
  );
};

export default TenantManagement;
